import request from '@utils/axiosWrapper';
import * as Ids from '@utils/ids';
import {
    CartDetailInfo,
    RawOrderBaseInfo,
    DispatcherEvaluationResult,
    PaotuiPoiRightsInfo,
    LogisticsStatusCode,
    RulesInfo,
    IorderPTRiderRankingInfo,
    IresponsibilityAppeal,
    LogisticsEvalutionResponse,
    DispatchExceptionCode,
} from '../types';

/** 一个经纬度坐标，为了保持精度，经纬度数值被放大了 1000000 倍 */
type Coordinate = {
    /** 经度 */
    longitude: number;
    /** 纬度 */
    latitude: number;
};

enum BlockTags {
    OrderInfo = 'orderInfo',
    UserInfo = 'userInfo',
    LogisticsInfo = 'logisticsInfo',
    MealInfo = 'mealInfo',
    MealAbnormalInfo = 'mealAbnormalInfo',
    RefundInfo = 'refundInfo',
    ReminderInfo = 'reminderInfo',
    CansunInfo = 'cansunInfo',
    ResponsibleInfo = 'responsibleInfo',
    FoodInfo = 'foodInfo',
    ChargeInfo = 'chargeInfo',
    UserCompensateInfo = 'userCompensateInfo',
}

/** 获取订单部分模块详情 */
const getOrderDetail = (params: { orderViewId: string; wmPoiId: number; blockTags: string }) => {
    return request.rawPostAsFormSubmit<{
        code: number;
        data: RawOrderBaseInfo;
        msg: string;
        serverTime: number;
    }>('/gw/api/order/mix/detail/common', {
        ...params,
    });
};

/** 查询用户真实手机号、收货地址 */
const getRealPhoneAddress = (params: { orderViewId: string; wmPoiId: number }) => {
    return request.postAsFormSubmit<{
        Recipient_Phone: string;
        Recipient_bindedPhone: string;
        Recipient_Address: string;
        Backup_Privacy_Phones: string[];
    }>('/v2/order/receive/processed/r/querywithin3h', {
        ...params,
    });
};
/** 查询订单距离信息 */
const getOrderDistanceInfo = (orderLatAndLngS: string) => {
    return request.get<Array<{ wm_order_id_view_str: string; distance: number }>>(
        '/v2/order/receive/processed/r/distribute/pathDistance/v2',
        {
            params: {
                orderLatAndLngS,
            },
        },
    );
};

/** 自配门店获取配送范围（接口返回的是JSON字符串） */
const getPoiShippingArea = (params: { wmPoiId: number; orderTime: number }) => {
    return request.get<string>('/api/poi/spArea', {
        params,
    });
};

type ConfirmOrderRawResponse = {
    msg: string;
    code: number;
    data: {
        distribute_tips?: string;
        cancel_reason: string;
        orderId: number;
        pay_status: number;
        pay_status_desc: string;
        status: number;
    };
};

type ConfirmOrderParams = {
    wmPoiId: number;
    orderId: string;
    acctId: string;
    token: string;
    isPrint: 0 | 1;
    appType: '3';
    isAutoAccept: 0 | 1;
};

/** 接单 */
const confirmOrder = (params: ConfirmOrderParams) => {
    return request.rawPostAsFormSubmit<ConfirmOrderRawResponse>(
        '/v2/order/receive/unprocessed/w/confirm',
        params,
    );
};

type PartialRefundFoodInfo = {
    boxNum: number;
    boxPrice: number;
    count: number;
    foodName: string;
    foodPrice: number;
    imageUrl: string;
    itemId: string;
    originFoodPrice: number;
    refundPrice: number;
    wmFoodId: number;
    cartId?: number;
    /** 使用了商品券的商品/套餐不支持部分退 */
    canNotPartRefund: boolean;
};

export type PartialRefundCartInfo = {
    cartId: number;
    cartName: string;
    showCartFee: boolean;
    cartFee: number;
};

type GetPartialRefundFoodListResponse = {
    marking: number;
    foodInfos: PartialRefundFoodInfo[];
    cartInfos?: PartialRefundCartInfo[];
    reasons?: string[];
    refundMoneyTip: string;
};

/** 获取部分退款商品列表 */
const getPartialRefundFoodList = (wmPoiId: number, wmOrderViewId: string) => {
    return request.get<GetPartialRefundFoodListResponse>('/v2/order/refund/partRefund/foodInfo', {
        params: {
            wmPoiId,
            wmOrderViewId,
        },
    });
};

export type PartialRefundCalculateFood = {
    itemId: string;
    wmFoodId: number;
    count: number;
};

/** 计算部分退款金额 */
const calculatePartialRefundAmount = (
    wmPoiId: number,
    wmOrderViewId: string,
    foodInfos: PartialRefundCalculateFood[],
) => {
    return request.postAsFormSubmit<{ refundMoney: number; hasBoxFee: boolean }>(
        '/v2/order/refund/partRefund/calPrice',
        {
            wmPoiId,
            wmOrderViewId,
            foodInfos: JSON.stringify(foodInfos),
        },
    );
};

/** 提交部分退款申请 */
const applyPartialRefund = (
    wmPoiId: number,
    wmOrderViewId: string,
    reason: string,
    foodInfos: PartialRefundCalculateFood[],
) => {
    return request.postAsFormSubmit('/v2/order/refund/partRefund/apply', {
        wmPoiId,
        wmOrderViewId,
        reason,
        foodInfos: JSON.stringify(foodInfos),
    });
};

/** 获取门店营业状态 */
const getPoiBusinessStatus = (poiId: number, orderId: string) => {
    return request.get<number>('/common/status', {
        params: {
            orderId,
            poiId,
        },
    });
};

type CancelOrderReason = {
    cat_id: number;
    id: number;
    remark: string;
    sort: 0;
};

/** 获取取消订单原因列表-不用了，后面删掉 */
const getCancelOrderReasonList = (wmPoiId: number, wmOrderViewId: string) => {
    return request.get<CancelOrderReason[]>('/v2/order/receive/cancelReasons/r/get', {
        params: {
            appType: '3',
            wmPoiId,
            wmOrderViewId,
        },
    });
};

type CancelOrderReasonsListResponse = {
    /** 0: 可识别&可校验;  1: 可校验&未联系顾客;  2: 不可校验 */
    type: 0 | 1 | 2;
    /** 弹框标题 */
    title: string;
    /** 弹框内容 */
    remindWord: string;
    /** 文案中超链接 */
    linkWord: string;
    link: string;
    reasonList: CancelOrderReason[];
};

/** 获取取消订单原因列表-新 */
const getCancelOrderReasonsList = (wmPoiId: number, wmOrderViewId: string, backendCheck: 0 | 1) => {
    return request.get<CancelOrderReasonsListResponse>(
        '/gw/v2/order/receive/cancelReasons/r/get/new',
        {
            params: {
                appType: '3',
                wmPoiId,
                wmOrderViewId,
                backendCheck,
            },
        },
    );
};

/** 获取取消订单时可同步下架的商品列表 */
const getCancelReasonFoodList = (wmPoiId: number, orderId: string) => {
    return request.postAsFormSubmit<Array<{ id: number; name: string }>>(
        '/api/order/cancelReasons/foods',
        {
            wmPoiId,
            orderId,
            token: Ids.getToken(),
            acctId: Ids.getAcctId(),
        },
    );
};

type CancelOrderParams = {
    appType: '3';
    token: string;
    acctId: string;
    orderId: string;
    wmPoiId: number;
    reasonId: number;
    subRadioId: string;
    remark: string;
    isInNewOrder: 1 | 0;
};

/** 取消订单 */
const cancelOrder = (params: Omit<CancelOrderParams, 'appType' | 'token' | 'acctId'>) => {
    return request.postAsFormSubmit('/v2/order/receive/unprocessed/w/cancel', {
        ...params,
        appType: '3',
        token: Ids.getToken(),
        acctId: Ids.getAcctId(),
    });
};

type ImCapability = {
    /** 用户侧APP版本是否支持 */
    userVersionSupport: number;
};
/** 查询商家侧发起IM能力 */
const getImCapability = (params: {
    acctId: string;
    uuid: string;
    wmPoiId: number;
    wmUserId: number;
    orderViewId: string;
}) => {
    return request.get<ImCapability>('/v2/chat/im/launch', { params });
};

/** 获取催单回复通用文案 */
const getOrderRemindExplaination = () => {
    return request.rawGet<{ data: string[] }>('/v2/order/customer/reminder/unprocessed/r/exlain');
};

type ReplyRemindBaseParams = {
    wmPoiId: number;
    remindId: number;
    response: string;
};

/** 通过IM回复催单 */
const replyRemindThroughIM = (
    params: ReplyRemindBaseParams & { acctId: string; userId: number; orderViewId: string },
) => {
    return request.postAsFormSubmit(
        '/v2/order/customer/reminder/unprocessed/w/im/response',
        params,
    );
};

/** 不通过IM回复催单 */
const replyRemindWithoutIM = (params: ReplyRemindBaseParams & { wmOrderViewId: string }) => {
    return request.postAsFormSubmit(
        '/v2/order/customer/reminder/unprocessed/w/response/v2',
        params,
    );
};

/** 上报出餐完成 */
const reportPrepareMealCompleted = (params: { wmPoiId: number; wmOrderViewId: string }) => {
    return request.postAsFormSubmit('/v2/common/w/reported/completeMealTime', params);
};

/** 餐损赔付申请详情 */
type LossCompensateDetail = {
    /** 赔付金额 */
    amount: number;
    /** 是否按比例赔付 */
    isDiscount: boolean;
    /** 赔付百分比 */
    discountRate: number;
};

/** 查询餐损赔付申请详情 */
const getLossCompensateDetailForOrder = (params: { wmPoiId: number; wmOrderViewId: string }) => {
    return request.get<LossCompensateDetail>(
        '/v2/order/receive/processed/r/distribute/payment/amount/v2',
        {
            params,
        },
    );
};

type SubmitLossCompensateApplyParams = {
    wmOrderViewId: string;
    wmPoiId: number;
    applicationReason: string;
    amount: number;
    applyStatus: number;
};

/** 提交 餐损赔付/赔付申诉 */
const submitLossCompensateApply = (params: SubmitLossCompensateApplyParams) => {
    return request.postAsFormSubmit(
        '/v2/order/receive/processed/w/distribute/payment/apply/v2',
        params,
    );
};

/** 撤销 餐损赔付/赔付申诉 */
const cancelLossCompensateApply = (params: { wmPoiId: number; wmOrderViewId: string }) => {
    return request.postAsFormSubmit(
        '/v2/order/receive/processed/w/distribute/payment/cancel/v2',
        params,
    );
};

/** 提交餐损赔付申请（非 SLA 新餐损） */
const submitCompensateApplication = (params: { wmPoiId: number; wmOrderViewId: string }) => {
    return request.postAsFormSubmit('/gw/api/order/compensate/apply', params);
};

type SubmitDutyCompensateApplyParams = {
    wmOrderViewId: string;
    wmPoiId: number;
    reason: string;
    picUrls: string;
};

/** 提交定责赔付申诉 */
const submitDutyCompensateApply = (params: SubmitDutyCompensateApplyParams) => {
    return request.postAsFormSubmit('/v2/order/confirm/responsibility/w/appeal', params);
};

/** 提交定责赔付申诉（非 SLA 新餐损） */
const submitDutyCompensateAppeal = (params: SubmitDutyCompensateApplyParams) => {
    return request.postAsFormSubmit('/gw/api/order/compensate/appeal', params);
};

/** 获取骑手电话列表 */
const getDispatcherPhoneList = (params: { wmOrderViewId: string; wmPoiId: number }) => {
    return request.get<{
        dispatcherPhone: string;
        dispatcherName: string;
        orgLeaderPhone: string;
        orgLeaderName: string;
        orgEmergencyPhone: string;
        orgEmergencyName: string;
        showTips: boolean;
        detailTips: string;
    }>('/v2/order/receive/processed/r/dispatcherPhone', {
        params,
    });
};

type DispatcherInformResponse = {
    /** 骑手id */
    riderId: string;
    /** 骑手姓名 */
    riderName: string;
    /** 当前商家拉黑骑手数量 */
    poiBlacklistCount: number;
    /** 当前骑手是否被拉黑 */
    inBlacklist: boolean;
    /** 是否达到最大值拉黑数量 */
    maxBlacklist: boolean;
    /** 最大可拉黑数量 */
    maxNum: number;
    phone: {
        dispatcherPhone: string;
        orgLeaderPhone: string;
        orgEmergencyPhone: string;
        showTips: boolean;
        detailTips: string;
    };
};

/** 获取骑手信息 */
const getDispatcherInformPhoneList = (params: { wmOrderViewId: string; wmPoiId: number }) => {
    return request.get<DispatcherInformResponse>('/gw/api/logistics/rider/detail', {
        params,
    });
};

type NoDispatcherPreConfirmInfoResponse = {
    suggestText: string;
    suggestTextTip: string;
};

/** 获取无人接单场景用户取消订单的预判责文案 */
const getNoDispatcherPreConfirmInfo = (params: { wmOrderViewId: string; wmPoiId: number }) => {
    return request.get<NoDispatcherPreConfirmInfoResponse>(
        '/gw/api/order/responsibility/noDispatcher/preConfirm',
        {
            params,
        },
    );
};

/** 获取配送状态变更日志 */
const getDeliveryStatusLog = (params: { wmPoiIdFromQuery: number; wmOrderViewId: string }) => {
    return request.rawGet<{
        code: number;
        data: Array<{
            timeLine: string;
            deliveryStatusDesc: string;
            picList: null | string[];
            /** 20-取件；40-送达 */
            logisticsStatus: number;
        }>;
        msg: string;
    }>('/api/logistics/logs', { params });
};

/** 获取骑手轨迹 */
const getDispatcherTraceInfo = (params: { wmOrderViewId: string; wmPoiId: number }) => {
    return request.get<Array<{ lat: number; lng: number }>>(
        '/v2/order/receive/r/distribute/getTraceInfo',
        { params },
    );
};

/** 骑手点击到店状态 */
type DispatcherArrivedStatus = {
    /** 骑手头像url */
    avatar: string;
    /** 骑手点击到店时距离门店的距离 */
    distance?: number;
    /** 允许骑手点击到店的距离门店的最大距离，超出此距离视为异常给商家提示 */
    limit: number;
    /** 门店头像url */
    shopPicUrl: string;
    /** 骑手点击到店位置的坐标 */
    locationInfo?: Coordinate;
    /** 配送状态 */
    logisticsStatus: LogisticsStatusCode | null;
    /** 骑手距离商家/用户直线距离（后端拼好的文案  789m/1.2km） */
    riderDistance: string | null;
    /** 骑手送达时间戳 */
    deliveryTime: string | null;
    /** 骑手电话 */
    dispatcherPhone: string | null;
    /** 站点电话 */
    orgLeaderPhone: string | null;
    /** 站点备用电话 */
    orgEmergencyPhone: string | null;
    /** 是否四轮订单标志 */
    fourWheelDeliveryOrder: boolean | null;
    /** 地图提示 */
    riderMapReminder: {
        /** 是否展示 */
        isShow: boolean;
        /** 提示文案 */
        reminderText: string;
        /** 操作按钮文案 */
        operatText: string;
    };
};
/** 获取骑手的到店状态信息 */
const getDispatcherArrivedStatus = (params: { wmOrderViewId: string; wmPoiId: number }) => {
    return request.get<DispatcherArrivedStatus>('/v2/order/receive/r/distribute/getArrivedStatus', {
        params,
    });
};

/** 发起配送（跑腿非灰度、三方、美配）*/
const sendDelivery = (params: {
    wmOrderViewId: string;
    wmPoiId: number;
    deliveryTeamId: number;
    tipAmount: number;
    shippingFee: number;
}) => {
    return request.rawPostAsFormSubmit<{ code: number; msg: string }>(
        '/v2/order/receive/processed/w/distribute/v2',
        params,
    );
};

/** 商家自配（点击”自己送餐“） */
const deliverBySelf = (params: { wmOrderViewId: string; wmPoiId: number }) => {
    return request.postAsFormSubmit('/v2/order/receive/processed/w/sent/v2', params);
};

/** 神抢手订单优化新增发票 Tip 信息 */
const getInvoiceWindowTip = (params: { wmPoiId: number; orderViewId: string }) => {
    return request.get('/gw/api/order/getInvoiceWindowTip', { params });
};

type PricingDetailsType = {
    /** 定价明细费用名称 */
    pricingName: string;
    /** 定价明细附加内容 */
    pricingSubName: string;
    /** 定价明细费用金额 */
    pricingValue: number;
    /** 原价（划线价） */
    originPriceValue?: number;
    /** 减免金额 */
    deductionPriceValue?: number;
    /** 减免描述 */
    deductionDescription?: string;
};

/** 跑腿-附近骑手高亮类型 */
enum PTRiderHighlight {
    NONE = 0,
    CONTENT = 1,
    CONTENT_DESC = 2,
}

type PtRiderNear = {
    /** 是否展示 */
    show: boolean;
    /** 高亮类型 */
    highlight: PTRiderHighlight;
    /** 标题 */
    title: string;
    /** 文案内容 */
    content: string;
    /** 骑手单位 */
    desc: string;
};

type WmBMLDetail = {
    /** 惊喜立减 id */
    userTaskId: string;
    /** 惊喜立减 taskId */
    taskId: string;
    /** 惊喜立减金额 */
    bmlAmount: number;
};

/** 惊喜立减说明文案 */
type WmBMLDesc = {
    /** 文案标题 */
    title: string;
    /** 文案内容，\n 字符表示换行 */
    text: string;
};

/** 跑腿发配送时可选择的服务品牌 */
type ZbDispatcher = {
    /** 服务品牌图标  */
    icon: string;
    /** 服务品牌名称 */
    name: string;
    /** 服务品牌Code */
    code: string;
    /** 服务品牌价格 */
    totalAmountStr: string;
    /** 实收金额 */
    payAmount: number;
    /** 服务品牌描述文案 */
    descriptions: string[];
    /** 卖点签 */
    sellingTag: string;
    /** 卖点签是否生效 */
    sellingValid: 1 | 0;
    /** 特品签 */
    specialTag: string;
    /** 特品签是否生效 */
    specialValid: 1 | 0;
    /** 默认是否选中 */
    selected: 1 | 0;
    /** 权益列表 */
    rightsDetailList?: {
        /** 权益 icon 图片 */
        orderIcon: string;
        /** 权益名称 */
        tipsContent: string;
        /** 权益文字颜色 */
        orderPageTitleColor: string;
        /** 权益背景色 */
        orderPageTitleBgColor: string;
        /** 权益说明文案 */
        tipsComment: string;
    }[];
    /** 骑手预计接单和送达时间 */
    riderExpect?: {
        /** 预计接单时间，支持占位。例如：预计{#}以内接单；预计{#}左右接单；等 */
        acceptTemplate: string;
        /** 预计接单时间，例如：5分钟；12:30；等 */
        acceptTimeStr: string;
        /** 预计送达时间，支持占位。例如：预计{#}以内送达 ；预计{#}左右送达；等 */
        deliveryTemplate: string;
        /** 预计送达时间，例如：5分钟；12:30；等 */
        deliveryTimeStr: string;
    };
    /** 推荐结果
     * recommendResult: 推荐配送code
     * recommendMsg: 推荐的理由
     */
    recommendLogistic: null | {
        recommendResult: string;
        recommendMsg: string;
    };
};

/** 跑腿灰度商家活动、优惠券、权益详情 */
type PtGrayActivityCouponInfo = {
    /** 选中活动的金额 */
    activityAmount: number;
    /** 选中活动的ID */
    activityId: string;
    /** 选中活动的名称 */
    activityName: string | null;
    /** 选中优惠券的金额 */
    couponAmount: number;
    /** 可选优惠券数量 */
    couponCount: number;
    /** 选中优惠券的ID */
    couponId: string;
    /** 选中优惠券的名称 */
    couponName: string | null;
    couponSource: number;
    couponViewId: string;
    /** 临时加价 */
    extraFee: number;
    /**非预约单为false不展示发单时间，预约单为true展示发单时间 */
    isShowSendOrderTime: boolean;
    /** 临时加价说明文案 */
    extraFeeReason: string;
    /** 基础运费 */
    shippingFeeBase: number;
    shippingFee: number;
    bmCityId: number;
    distance: number;
    /** 跑腿资源位 */
    zbResourceInfoList?: Array<{
        resource_id: number;
        pic_url: string;
        click_url: string;
    }>;
    /** @deprecated 跑腿权益 */
    logisticsRightDesc?: PaotuiPoiRightsInfo;
    /** 跑腿权益列表 */
    logisticsRightDescList?: PaotuiPoiRightsInfo[];
    /** 高价提醒 */
    highPricePrompt?: string;
    /** 跑腿-定价明细列表 */
    pricingDetails?: PricingDetailsType[];
    /** 跑腿-附近骑手模块 */
    nearRiderNum: PtRiderNear;
    /** 跑腿-预计接单模块 */
    riderAcceptTime: PtRiderNear;
    /** 跑腿-接单慢文案 */
    acceptSlowMsg: string;
    /** 跑腿-预计送达时间 */
    riderDeliveredTime: PtRiderNear;
    /** 跑腿-惊喜立减详情 */
    wmBMLDetail?: WmBMLDetail;
    /** 跑腿-惊喜立减说明文案 */
    wmBMLDesc?: WmBMLDesc;
    zbDispatcher?: {
        /** 是否展示服务品牌选择 */
        showed: boolean;
        supportLogistics: Array<ZbDispatcher>;
    };
    /** 溢价费用详情（JSON 序列化后的字符串，前端无需关心具体内容，发配送时传回即可） */
    rightsDetailList?: string;
    paoTuiPayMode: number;
    /** 实付金额 */
    payAmount: number;
    /**
     * 1:立即罚单 2:系统推荐发单 0 商家第一次点击
     */
    sendOrderType: 1 | 2 | 0;
};

type GetPtGrayActivityCouponInfoParams = (
    | {
          wmOrderViewId: string;
          wmPoiId: number;
          tipAmount: number;
          activityId: string;
          couponId: string;
      }
    | {
          wmOrderViewId: string;
          wmPoiId: number;
          tipAmount: number;
          activityId: string;
          couponViewId: string;
          couponSource: number;
          wmBMLDetail?: WmBMLDetail;
      }
) & {
    /** 用于区分是否是 preview （点击「美团跑腿」按钮）询价还是切换优惠券后的询价 */
    previewFlag: boolean;
};

/** 跑腿灰度商家查询活动、优惠券信息 */
const getPtGrayActivityCouponInfo = (params: GetPtGrayActivityCouponInfoParams) => {
    return request.rawGet<{
        code: number;
        msg: string;
        data: PtGrayActivityCouponInfo;
    }>('/v2/order/receive/query/r/pt/activity/info', {
        params,
    });
};

type payMethodInfo = {
    accountAmount: number;
    payMode: number;
    rechargeLink: string;
    /**支付方式提醒 */
    paotuiPaymentTips?: string;
};
/** 获取支付方式信息 */
const getPayMethodInfo = (params: { wmPoiId: number }) => {
    return request.rawGet<{
        code: number;
        msg: string;
        data: payMethodInfo;
    }>('/gw/api/logistics/zb/payInfo', {
        params,
    });
};

/** 设置跑腿扣款顺序 */
const savePaymentOrder = (params: { wmPoiId: number; payMode: number }) => {
    return request.rawPostAsFormSubmit<{
        code: number;
        msg: string;
        data: boolean;
    }>('/gw/api/logistics/zb/payInfo/save', params);
};

/** 请求跑腿资源位banner列表 */
const getZhongbaoResourceInfo = (params: { wmPoidId: number }) => {
    return request.get<{
        zbResourceInfoList: Array<{ resource_id: number; pic_url: string; click_url: string }>;
    }>('/gw/v2/order/receive/query/r/pt/resource/info', { params });
};

/** 获取小费选项列表 */
const getDeliveryTipOptions = () => request.get<number[]>('/gw/api/tipFee/config');

type TipOptionWithRecommendation = {
    /** 单位：分 */
    money: number;
    recommend: 0 | 1;
};

/** 获取（带推荐的）小费选项列表 */
const getDeliveryTipOptionsWithRecommendation = (params: {
    wmPoiId: number;
    wmOrderViewId: string;
}) => request.get<TipOptionWithRecommendation[]>('/gw/api/logistics/tipFee/config', { params });
export type TypesendOrderType = 0 | 1 | 2;
/** 发起跑腿灰度 */
const sendZhongbaoForPtGray = (params: {
    wmOrderViewId: string;
    wmPoiId: number;
    tipAmount: number;
    shippingFee: number;
    couponParam: string;
    wmBMLDetail?: string;
    selectLogisticsCode?: string;
    rightDisToken?: string;
    rightsDetailList?: string;
    sendOrderType?: TypesendOrderType;
}) => {
    return request.rawPostAsFormSubmit('/v2/order/receive/processed/w/pt/distribute', params);
};

/** 获取跑腿非灰度配送费预览 */
const getZhongbaoNormalShippingFee = (
    orderInfos: Array<{ wmOrderViewId: string; wmPoiId: number }>,
) => {
    return request.get<Array<{ wm_order_view_id: string; shipping_fee: number }>>(
        '/v2/order/receive/processed/r/distribute/shippingFee/v2',
        {
            params: {
                orderInfos: JSON.stringify(orderInfos),
            },
        },
    );
};

type QuerySanfangShippingFeeResponse = {
    distance: number;
    /** 实付金额 */
    shippingFee: number;
    /** 费用明细 */
    shippingFeeDetail: Array<{
        /** 费用金额 */
        amount: number;
        /** 费用项 */
        shippingFeeDesc: string;
    }>;
    /** 预计送达时间 */
    estimatedDeliveryTime: string;
};

/** 三方配询价 */
const querySanfangShippingFee = (params: {
    wmOrderViewId: string;
    wmPoiId: number;
    thirdPartySpecificLogisticsId: number;
    optimizeVersion: 1;
}) => {
    return request.postAsFormSubmit<QuerySanfangShippingFeeResponse | null>(
        '/v2/order/receive/processed/r/third/distribution/preview',
        params,
    );
};

/** 获取取消配送的原因列表 */
const getCancelDeliveryReasonList = (params: { wmOrderViewId: string; wmPoiId: number }) => {
    return request.get<{
        title: string;
        deliveryStatus: number;
        reasonList: Array<{ code: string; content: string }>;
    }>('/v2/order/receive/processed/r/distribute/cancel/reason/v2', {
        params,
    });
};

type CancelDeliveryParams = {
    wmOrderViewId: string;
    wmPoiId: number;
    deliveryStatus: number | string;
    code: string;
    content: string;
    otherReason: string;
    optimizeVersion: number;
};
/** 取消配送 */
const cancelDelivery = (params: CancelDeliveryParams) => {
    return request.rawPostAsFormSubmit<{ code: number; msg: string }>(
        '/v2/order/receive/processed/w/distribute/cancel/v2',
        params,
    );
};
/** 取消配送二次确认 */
const cancelDeliveryConfirm = (params: Omit<CancelDeliveryParams, 'optimizeVersion'>) => {
    return request.rawPostAsFormSubmit<{ code: number; msg: string }>(
        '/v2/order/receive/processed/w/distribute/cancel/confirm/v2',
        params,
    );
};

/** 三方配取消配送异步查询结果 */
const querySanfangCancelDeliveryResult = (
    params: Omit<CancelDeliveryParams, 'optimizeVersion' | 'deliveryStatus'>,
) => {
    return request.rawPostAsFormSubmit<{ code: number; msg: string }>(
        '/v2/order/receive/processed/w/distribute/cancel/queryResult',
        params,
    );
};

export interface SelfShippingModalInfo {
    subTitle: string;
    tips: string;
    flexibleSelfLogisticsVo: {
        title: string;
        tipContent: string;
        restCount: number;
        totalCount: number;
        ruleContent: {
            title: string;
            content: string;
        };
    };
}

export const getSelfShippingModalInfo = (params: {
    /** 支持青云传 0，不支持青云传 1。目前 PC 无青云，固定传 1 即可 */
    popType: 0 | 1;
    wmPoiId: number;
    orderViewId: string;
}) => {
    return request.get<SelfShippingModalInfo>('/gw/api/logistics/self/window', { params });
};

/** 发起自配 */
const changeToPoiSelfShipping = (params: { wmOrderViewId: string; wmPoiId: number }) => {
    return request.postAsFormSubmit(
        '/v2/order/receive/processed/w/distribute/changeToPoiSelfShipping',
        params,
    );
};

/** 查询骑手评价数据 */
const getDispatcherEvaluationData = (orderInfos: string) => {
    return request.rawGet<{
        code: number;
        evaluationList: DispatcherEvaluationResult[];
        structureds: { [index: number]: string };
    }>('/v2/order/receive/processed/r/distribute/evaluation/v2', {
        params: {
            orderInfos,
        },
    });
};

/**
 * 评价详情查询接口-新
 * @wiki https://yapi.sankuai.com/project/20566/interface/api/1067878
 */
const getDispatcherEvaluationDataNew = (
    wmOrderViewId: string,
    wmPoiId: number,
    evaluateType: number,
) => {
    return request.get<LogisticsEvalutionResponse>('/gw/api/logistics/evaluation/search/v2', {
        params: {
            wmOrderViewId,
            wmPoiId,
            evaluateType,
        },
    });
};

/** 提交对骑手的评价 */
const saveDispatcherEvaluation = (params: {
    wmOrderViewId: string;
    wmPoiId: number;
    grade: number;
    personal: string;
    structuredCodes: string;
}) => {
    return request.postAsFormSubmit(
        '/v2/order/receive/processed/w/distribute/evaluation/v2',
        params,
    );
};

/**
 * 提交对骑手的评价-新
 * @wiki https://yapi.sankuai.com/project/20566/interface/api/1067878
 */
const saveDispatcherEvaluationNew = (evaluationList: string, wmPoiId: number) => {
    return request.postAsFormSubmit('/gw/api/logistics/evaluation/add/v2', {
        evaluationList,
        wmPoiId,
    });
};

/** 跑腿、快送、三方加小费 */
const updateShippingTips = (params: {
    wmOrderViewId: string;
    wmPoiId: number;
    tipAmount: number;
    optimizeVersion: number;
    shippingFee?: number;
    specificLogisticsId?: number;
}) => {
    return request.postAsFormSubmit(
        '/v2/order/receive/processed/w/distribute/tipAmount/v2',
        params,
    );
};

/** 三方配修改小费之前查询小费信息 */
const querySanfangTipsInfo = (params: { wmOrderViewId: string; wmPoiId: number }) => {
    return request.get<{
        tipAmount: number;
        tipfees: number[];
        shippingFee: number;
    }>('/v2/order/receive/processed/r/third/query/tipfee', {
        params,
    });
};

/** 三方配查询加小费结果 */
const querySanfangUpdateTipsResult = (params: {
    wmPoiId: number;
    wmOrderViewId: string;
    tipAmount: number;
}) => {
    return request.postAsFormSubmit(
        '/v2/order/receive/processed/w/distribute/tipAmount/queryResult',
        params,
    );
};

/** 提交投诉骑手 */
const submitDispatcherComplication = (params: {
    wmOrderViewId: string;
    wmPoiId: number;
    dispatcherName: string;
    dispatcherMobile: string;
    type: number;
    pics: string;
}) => {
    return request.postAsFormSubmit(
        '/v2/order/receive/processed/w/distribute/reportDeliveryException',
        params,
    );
};

/** 获取退款预判责信息 */
const getRefundPreResponsiblityInfo = (params: { wmPoiId: number; wmOrderViewId: string }) => {
    return request.get<{
        /** 是否需要转人工定责 0=不需要,1=需要 */
        if_need_manual: 0 | 1;
        /** 责任方数字描述 */
        responsible_party: number;
        /** 责任方文字描述 */
        responsiblePartyDesc: string;
        /** 具体的定责原因 */
        reason: string;
        wm_order_view_id: string;
    } | null>('/v2/order/confirm/responsibility/r/preConfirm', {
        params,
    });
};

/** 获取拒绝退款的原因列表 */
const getRejectRefundReasonList = (params: {
    wmPoiId: number;
    orderId: string;
    logisticsCode: string;
    logisticsStatus: number;
}) => {
    return request.get<{ rejectReasonTips: string; rejectReasonList?: string[] }>(
        '/v2/order/refund/unprocessed/r/rejectReason',
        {
            params,
        },
    );
};

/** 提交拒绝退款 */
const submitRejectRefund = (params: { wmPoiId: number; orderId: string; rejectReason: string }) => {
    return request.postAsFormSubmit('/v2/order/refund/unprocessed/w/reject', {
        ...params,
        token: Ids.getToken(),
        acctId: Ids.getAcctId(),
        appType: 3,
    });
};

/** 同意退款 */
const submitAgreeRefund = (params: { wmPoiId: number; orderId: string }) => {
    return request.postAsFormSubmit('/v2/order/refund/unprocessed/w/agree', {
        ...params,
        token: Ids.getToken(),
        acctId: Ids.getAcctId(),
        appType: 3,
    });
};

/**
 * （大纠纷）退款申诉
 *
 * 商家拒绝退款 -> 用户申请平台介入 -> 平台同意退款 -> 商家申诉
 */
const submitRefundAppeal = (params: {
    wmPoiId: number;
    orderViewId: string;
    afterSaleId: string;
    appealReason: string;
    appealPictures?: string;
}) => {
    return request.postAsFormSubmit('/gw/api/w/afterSale/issue/reject/poi/appeal', {
        ...params,
        token: Ids.getToken(),
        acctId: Ids.getAcctId(),
        appType: 3,
    });
};

/**
 * （大纠纷）同意平台（袋鼠分）介入结果（同意用户退款）
 *
 * 商家拒绝退款 -> 用户申请平台介入 -> 平台同意退款 -> 商家不申诉
 */
const approveCreditScoreJudgement = (params: {
    wmPoiId: number;
    orderViewId: string;
    afterSaleId: string;
}) => {
    return request.postAsFormSubmit('/gw/api/w/afterSale/issue/reject/poi/notAppeal', {
        ...params,
        token: Ids.getToken(),
        acctId: Ids.getAcctId(),
        appType: 3,
    });
};

/** 小额转账并拒绝退款 */
export const transferAmountAndRejectRefund = (
    wmPoiId: number,
    wmOrderViewId: string,
    money: number,
) => {
    return request.rawPostAsFormSubmit<{ code: number; message: string }>(
        '/gw/api/order/afterSale/recover/transferAmount',
        {
            wmPoiId,
            wmOrderViewId,
            money,
        },
    );
};

/** 部分退款前并拒绝全额退款 */
export const rejectFullRefundBeforePartialRefund = (wmPoiId: number, wmOrderViewId: string) => {
    return request.rawPostAsFormSubmit<{ code: number; message: string }>(
        '/gw/api/order/afterSale/recover/partRefund',
        {
            wmPoiId,
            wmOrderViewId,
        },
    );
};

/** 转自配并拒绝退款 */
export const changeToPoiSelfShippingAndRejectRefund = (wmPoiId: number, wmOrderViewId: string) => {
    return request.rawPostAsFormSubmit<{ code: number; message: string }>(
        '/gw/api/order/afterSale/recover/toSelfDispatch',
        {
            wmPoiId,
            wmOrderViewId,
        },
    );
};

export { CartDetailInfo };
export const getResendFoodInfo = (wmPoiId: number, wmOrderViewId: string) => {
    return request.get<CartDetailInfo[]>('/gw/api/order/refund/partRefund/foodInfo', {
        params: { wmPoiId, wmOrderViewId },
    });
};

export interface ResendMealFoodInfo {
    wmFoodId: number;
    foodName: string;
    count: number;
}

/** 补发餐品 */
export const resendMeal = (
    wmPoiId: number,
    wmOrderViewId: string,
    foodInfos: ResendMealFoodInfo[],
) => {
    return request.rawPostAsFormSubmit<{ code: number; message: string }>(
        '/gw/api/order/afterSale/recover/resendMeal',
        {
            wmPoiId,
            wmOrderViewId,
            foodInfos: JSON.stringify(foodInfos),
        },
    );
};

/**
 * 极速退-小美评审 申诉
 *
 */
const instantRefundAppeal = (params: {
    wmPoiId: number;
    orderViewId: string;
    afterSaleId: string;
    appealReason: string;
    appealPictures?: string;
}) => {
    return request.postAsFormSubmit('/gw/api/w/instantRefund/poi/appeal', {
        ...params,
        token: Ids.getToken(),
        acctId: Ids.getAcctId(),
        appType: 3,
    });
};

/** 请求门店是否命中卡餐上报 */
const getMakeMealSlowReportGrayStatus = () => {
    return request.get<boolean>('/gw/api/r/waitMeal/poi/gray/new');
};

/** 门店卡餐状态信息 */
type MakeMealSlowState = {
    /** 无卡餐生效时的功能引导文案 */
    configText: string;
    /** 主动上报按钮是否处于禁用状态（是否有卡餐生效中）1是，0否 */
    buttonForbidden: 0 | 1;
    /** 卡餐生效中提示文案 */
    optioninfo: string;
    /** 系统当前时间 */
    currentTimestamp: number;
    /** 当前卡餐状态结束时间 */
    endTimestamp: number;
    /** 是否属于全天候出餐后调度商家 */
    allDayDispatchAfterFinishCook: boolean;
    /** 是否是出餐后调度 */
    dispatchAfterFinishCook: boolean;
    /** 关闭卡餐文案 */
    closeKcText: string;
    /** 关闭卡餐按钮是否展示 */
    closeKcButton: boolean;
};
/** 请求卡餐状态 */
const getMakeMealSlowState = () => {
    return request.get<MakeMealSlowState>('/gw/api/r/waitMeal/index/config/get');
};
/** 关闭卡餐 */
const closeKC = () => {
    return request.rawPost('/gw/api/v2/w/report/waitMeal/close');
};
type ExtendMakeMealTimeOption = {
    subOperationType: number;
    subOperationName: string;
};

type EffectTimeOption = {
    value: number;
    name: string;
};

// 出餐后分配骑手信息
type IrecommendOperation = {
    /** 是否显示出餐后分配骑手选项 */
    showDispatchAfterFinishCook: boolean;
    /** 选项文案 */
    text: string;
    /** 选项提示 */
    tips: string;
};

// 立即出餐信息
type IoperationInfo = {
    /** 选项文案 */
    operationName: string;
    /** 选项提示 */
    tips: string;
    selectTips: string;
};

type Icontent = {
    title: string;
    type: string;
    content: Array<{
        content: string;
        label: string;
        subContent: string;
        type: string;
    }>;
};
type IinstructionContent = {
    content: Array<{
        content: Array<Icontent>;
        title: string;
        type: string;
    }>;
    title: string;
};

type GetReportMakeMealSlowOptionsResponse = {
    instructionContent: IinstructionContent;
    title: string;
    recommendOperation: IrecommendOperation;
    /** 生效时间 */
    effectTime: {
        /** 选项 */
        operation: EffectTimeOption[];
        /** 默认值 */
        defaultValue: EffectTimeOption;
    };
    /** 支持的处理方式，原来有多重处理方式，现在只有延长出餐时间，为了保持兼容性，依然是一个数组 */
    operation: Array<{
        operationType: number;
        operationName: string;
        tips: string;
        selectTips: string;
        defaultValue: ExtendMakeMealTimeOption;
        subOperation: ExtendMakeMealTimeOption[];
    }>;
};

const getReportMakeMealSlowOptions = (params: {
    source: number;
    orderViewId?: string;
    tag?: string;
}) => {
    return request.get<GetReportMakeMealSlowOptionsResponse>('/gw/api/v2/r/waitMeal/option', {
        params,
    });
};

const submitMakeMealSlowReport = (params: {
    sceneType: number;
    extendMakeMealTimeType: number;
    effectTime: number;
    expireTime?: string;
    balanceUuid?: string;
    dispatchAfterFinishCook?: boolean;
}) => {
    const data: any = {
        sceneType: params.sceneType,
        action: JSON.stringify([
            {
                operationType: 1,
                subOperationType: [params.extendMakeMealTimeType],
            },
        ]),
        effectTime: params.effectTime,
    };
    if (params.expireTime) {
        data.expireTime = params.expireTime;
    }

    if (params.balanceUuid) {
        data.balanceUuid = params.balanceUuid;
    }

    if (params.dispatchAfterFinishCook) {
        data.dispatchAfterFinishCook = params.dispatchAfterFinishCook;
    }
    return request.postAsFormSubmit('/gw/api/v2/w/report/waitMeal', data);
};

type GetInsuranceRefundAdviceResponse = {
    /** 标题 */
    title: string;
    /** 自主售后理赔金额 */
    expectMoney?: number;
    /** 内容 */
    content: Array<{
        /** 标题 */
        label: string;
        /** 内容 */
        value: string;
    }>;
    /** 赔付规则 */
    refundRule: {
        /** 标题 */
        name: string;
        /** 规则内容地址（图片） */
        detailPic: string;
    };
    /** 保障条款地址 */
    specUrl: string;
};

/** 餐损险保障详情 */
const getInsuranceRefundAdvice = () => {
    return request.postAsFormSubmit<GetInsuranceRefundAdviceResponse>(
        '/gw/api/order/insurance/refundTips',
        {
            acctId: Ids.getAcctId(),
            wmPoiId: Ids.getWmPoiId(),
            appType: 3,
        },
    );
};

/**
 * 催取餐
 */
const urgeMeal = (params: { orderViewId: string; wmPoiId: number }) => {
    return request.post('/gw/api/logistics/urging', {
        acctId: Ids.getAcctId(),
        token: Ids.getToken(),
        ...params,
    });
};

/**
 * 售后信息规则查询
 * @param params 0.退款建议说明 1.申诉规则
 * @returns
 */
const getRules = (sourceType: number, orderViewId?: string, wmPoiId?: number) => {
    return request.get<RulesInfo>('/gw/api/order/saleInfo/rules', {
        params: { sourceType, orderViewId, wmPoiId },
    });
};

export interface RulesInfoV2 {
    rules?: {
        title: string;
        label: string;
        detail?: {
            content: string;
            desc?: string[];
        }[];
    }[];
    sla: boolean;
}

export const getRulesV2 = (wmPoiId: number, wmOrderViewId: string) => {
    return request.get<RulesInfoV2>('/gw/api/order/saleInfo/rules/v2', {
        params: { wmPoiId, wmOrderViewId },
    });
};

type PtShipFeeRulesResponse = {
    /** 页面title */
    pageTitle: string;
    /** 是否展示计价规则 */
    pageShow: boolean;
    /** */
    pageRules: {
        /** 明细标题 */
        ruleTitle: string;
        /** 明细详情 */
        ruleItems: {
            /** 左侧文案 */
            itemLText: string;
            /** 右侧文案 */
            itemRText: string;
        }[];
    }[];
};
/**
 * 跑腿-获取计价规则
 */
const ptShipFeeRules = (
    wmOrderViewId: string,
    wmPoiId: number,
    selectLogisticsCode?: string | null,
) => {
    const params: any = { wmOrderViewId, wmPoiId };
    if (selectLogisticsCode) {
        params.selectLogisticsCode = selectLogisticsCode;
    }
    return request.get<PtShipFeeRulesResponse>('/gw/api/logistics/pt/shipFeeRules', {
        params,
    });
};

/**
 * 跑腿
 * 拉黑骑手
 */
const riderBlacklistPut = (params: {
    riderId: string;
    riderName: string;
    wmPoiId: number;
    orderViewId: string;
}) => {
    return request.rawPostAsFormSubmit('/gw/api/logistics/rider/blacklist/put', {
        ...params,
    });
};

/**
 * 跑腿
 * 解除拉黑骑手
 */
const riderBlacklistRemove = (params: { riderId: string; riderName: string; wmPoiId: number }) => {
    return request.rawPostAsFormSubmit('/gw/api/logistics/rider/blacklist/remove', {
        ...params,
    });
};

/**
 * 跑腿
 * 查询骑手排队信息
 */
const riderRankingInfo = (pressureParam: string) => {
    return request.get<IorderPTRiderRankingInfo>('/gw/api/order/receive/pt/rider/pressure', {
        params: { pressureParam },
    });
};

/**
 * 是否取消配送（无法履约场景）
 * @wiki http://yapi.sankuai.com/project/20566/interface/api/1051848
 */
const distributeResend = (params: {
    wmPoiId: number;
    wmOrderViewId: string;
    needPushDelivery: 0 | 1;
}) => {
    return request.postAsFormSubmit('/gw/api/logistics/deliveryException/distribute/resend', {
        ...params,
    });
};

/**
 * 四轮配送
 * @wiki https://yapi.sankuai.com/project/20566/interface/api/1124834
 */
const distributeFourRounds = (params: {
    wmPoiId: number;
    wmOrderViewId: string;
    /** 操作类型 0.拒绝 1.同意 */
    status: 0 | 1;
}) => {
    return request.get('/gw/api/logistics/fourRoundsDelivery/confirm', {
        params,
    });
};

/**
 * 获取申诉页面信息
 * @wiki https://km.sankuai.com/collabpage/1372560522#id-4.1.1%20%E5%95%86%E5%AE%B6%E9%A4%90%E6%8D%9F%E7%94%B3%E8%AF%89%E9%A1%B5%E9%9D%A2%E4%BF%A1%E6%81%AF%E4%BC%98%E5%8C%96
 */
const getResponsibilityAppeal = (wmPoiId: number, wmOrderViewId: string) => {
    return request.get<IresponsibilityAppeal>('/gw/api/order/responsibility/appeal/tips', {
        params: { wmOrderViewId, wmPoiId },
    });
};

type WeatherResponse = {
    showFlag: number;
    weatherTitle: string;
    weatherSubTitle: string;
    /**
     * 天气类型
     *
     * - `1` 中雨
     * - `2` 大雨
     * - `3` 中雪
     * - `4` 大雪
     * - `-1` 未知
     */
    weatherType: 1 | 2 | 3 | 4 | -1;
    temperature: string;
    qaList: {
        question: string;
        answer: string;
    }[];
};

const getWeather = () =>
    request.get<WeatherResponse>('/gw/api/poi/weather', {
        params: {
            wmPoiId: Ids.getWmPoiId(),
            token: Ids.getToken(),
            acctId: Ids.getAcctId(),
            appType: 3,
        },
    });

/**
 * ETA修改模块，商家点击【同意修改】或【拒绝修改】时获取弹窗内容
 */
const getETAChangeOperationWindowContent = (params: {
    /** 弹框类型：0 同意、1 拒绝 */
    windowType: 0 | 1;
    uetaTime: string;
    wmPoiId: number;
    wmOrderViewId: string;
}) => {
    return request.postAsFormSubmit<{
        title: string;
        contentList: Array<{
            text: string;
            /** 默认0-无样式，1-高亮，2-xx颜色（找PM对齐色号，红色？），3-换行 */
            textShowType: 0 | 1 | 2 | 3;
        }>;
        winLeftBtn: boolean;
        winLeftBtnText: string;
        winRightBtn: boolean;
        winRightBtnText: string;
    } | null>('/gw/order/eta/modify/window', params);
};

/**
 * ETA修改模块，商家同意修改
 */
const agreeETAChange = (params: { wmPoiId: number; wmOrderViewId: string }) => {
    return request.rawPostAsFormSubmit<{ code: number; msg: string }>(
        '/gw/order/eta/modify/agree',
        params,
    );
};

/**
 * ETA修改模块，商家拒绝修改
 */
const refuseETAChange = (params: { wmPoiId: number; wmOrderViewId: string }) => {
    return request.rawPostAsFormSubmit<{ code: number; msg: string }>(
        '/gw/order/eta/modify/refuse',
        params,
    );
};

type mealProvePageInfoResponse = {
    /** 换骑手最大次数 */
    totalTimes: number;
    /** 当前换骑手次数 */
    currentTimes: number;
    /** 换骑手预计时间 */
    expectTime: number;
};
/** 获取真实出餐证明 */
const getMealProvePageInfo = (params: { wmPoiId: number; wmOrderViewId: string }) =>
    request.get<mealProvePageInfoResponse>('/gw/api/order/queryMealProvePageInfo', {
        params,
    });
type UserCompensateApplyQueryResult = {
    inServiceHallGray: boolean;
    appealInfoServiceUrl: string;
    showApplyBtnTime: number;
};

/**
 * 【赔付顾客】模块，获取赔付申诉入口
 */
const getApplyEntraceInfo = (params: { wmPoiId: number; orderViewId: string }) => {
    return request.rawGet<{
        code: number;
        data: UserCompensateApplyQueryResult;
        msg: string;
    }>('/gw/api/order/user/compensate/getApplyEntraceInfo', { params });
};

type ExamineInstructionResponse = {
    title: string;
    type: string;
    content: {
        title: string;
        type: string;
        picUrl?: string | null;
        content: {
            type: string;
            label: string;
            content: string;
            subContent: string;
        }[];
    }[];
};
/**
 * 出餐表现考核说明 弹窗文本接口
 * type: 0 出餐表现考核说明； 1 出餐异常情况帮助说明‘ 2、出餐豁免情况说明 3、待出餐出餐豁免情况帮助说明 4、上报卡餐常见问题
 */
const getExamineInstruction = (type: 0 | 1 | 2 | 3 | 4 | 5, wmPoiId: number) =>
    request.get<{ title: string; content: ExamineInstructionResponse[] }>(
        '/gw/api/prepMeal/examine/instruction',
        {
            params: {
                type,
                wmPoiId,
            },
        },
    );

type IConfirmNoMealByWmlParams = {
    /** 门店id */
    wmPoiId: number;
    /** 异常类型 */
    exception_type:
        | DispatchExceptionCode.DISPATCH_ZI_PEI
        | DispatchExceptionCode.DISPATCH_SELF_PICK
        | DispatchExceptionCode.DISPATCH_SELL_OUT;
    /** 结果 */
    result: 0 | 1;
    /** 订单id */
    orderViewId: string;
};
/** 商家确认是否无餐 */
const confirmNoMealByWm = (data: IConfirmNoMealByWmlParams) =>
    request.rawPostAsFormSubmit<{
        data: boolean;
        code: number;
        msg: string;
    }>('/gw/api/order/nomeal/confirm', data);

export {
    getRealPhoneAddress,
    getOrderDistanceInfo,
    getPoiShippingArea,
    ConfirmOrderRawResponse,
    confirmOrder,
    getPartialRefundFoodList,
    PartialRefundFoodInfo,
    calculatePartialRefundAmount,
    applyPartialRefund,
    getPoiBusinessStatus,
    getCancelOrderReasonList,
    getCancelOrderReasonsList,
    CancelOrderReason,
    getCancelReasonFoodList,
    cancelOrder,
    getImCapability,
    getOrderRemindExplaination,
    replyRemindThroughIM,
    replyRemindWithoutIM,
    reportPrepareMealCompleted,
    LossCompensateDetail,
    getLossCompensateDetailForOrder,
    submitLossCompensateApply,
    cancelLossCompensateApply,
    submitCompensateApplication,
    submitDutyCompensateApply,
    submitDutyCompensateAppeal,
    getDispatcherPhoneList,
    getDeliveryStatusLog,
    getDispatcherTraceInfo,
    getDispatcherArrivedStatus,
    Coordinate,
    DispatcherArrivedStatus,
    sendDelivery,
    deliverBySelf,
    PtGrayActivityCouponInfo,
    getPtGrayActivityCouponInfo,
    getDeliveryTipOptions,
    TipOptionWithRecommendation,
    getDeliveryTipOptionsWithRecommendation,
    sendZhongbaoForPtGray,
    getZhongbaoNormalShippingFee,
    querySanfangShippingFee,
    QuerySanfangShippingFeeResponse,
    getCancelDeliveryReasonList,
    cancelDelivery,
    cancelDeliveryConfirm,
    querySanfangCancelDeliveryResult,
    changeToPoiSelfShipping,
    getDispatcherEvaluationData,
    saveDispatcherEvaluation,
    updateShippingTips,
    querySanfangTipsInfo,
    querySanfangUpdateTipsResult,
    submitDispatcherComplication,
    getRefundPreResponsiblityInfo,
    getRejectRefundReasonList,
    submitRejectRefund,
    submitAgreeRefund,
    submitRefundAppeal,
    approveCreditScoreJudgement,
    getZhongbaoResourceInfo,
    getMakeMealSlowReportGrayStatus,
    getMakeMealSlowState,
    closeKC,
    getReportMakeMealSlowOptions,
    ExtendMakeMealTimeOption,
    EffectTimeOption,
    submitMakeMealSlowReport,
    getInsuranceRefundAdvice,
    GetInsuranceRefundAdviceResponse,
    urgeMeal,
    IoperationInfo,
    IrecommendOperation,
    getRules,
    PricingDetailsType,
    ptShipFeeRules,
    PtShipFeeRulesResponse,
    getDispatcherInformPhoneList,
    riderBlacklistPut,
    riderBlacklistRemove,
    riderRankingInfo,
    getOrderDetail,
    BlockTags,
    PtRiderNear,
    getNoDispatcherPreConfirmInfo,
    ZbDispatcher,
    distributeResend,
    getResponsibilityAppeal,
    IresponsibilityAppeal,
    WeatherResponse,
    getWeather,
    getDispatcherEvaluationDataNew,
    saveDispatcherEvaluationNew,
    getETAChangeOperationWindowContent,
    agreeETAChange,
    refuseETAChange,
    distributeFourRounds,
    instantRefundAppeal,
    getMealProvePageInfo,
    getApplyEntraceInfo,
    getPayMethodInfo,
    savePaymentOrder,
    payMethodInfo,
    getExamineInstruction,
    ExamineInstructionResponse,
    getInvoiceWindowTip,
    confirmNoMealByWm,
    IinstructionContent,
    Icontent,
};
